import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Check, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SubscriptionPlan } from '@/services/subscription/subscription.service';

interface PlanCardProps {
  plan: SubscriptionPlan;
  isSelected?: boolean;
  isCurrentPlan?: boolean;
  canUpgrade?: boolean;
  onSelect?: () => void;
}

export function PlanCard({ plan, isSelected, isCurrentPlan, canUpgrade, onSelect }: PlanCardProps) {
  const { t } = useTranslation();

  const features = plan.features as {
    storage?: number;
    ram?: number;
    unlimitedUsers?: boolean;
    maxStaffUsers?: number;
  } | undefined;

  const isDisabled = !canUpgrade && !isCurrentPlan;

  return (
    <Card
      className={cn(
        'relative transition-all cursor-pointer',
        isSelected && 'border-blue-500 ring-2 ring-blue-200',
        isDisabled && 'opacity-50 cursor-not-allowed',
        isCurrentPlan && 'border-emerald-500'
      )}
      onClick={!isDisabled ? onSelect : undefined}
    >
      {isCurrentPlan && (
        <div className="absolute top-4 right-4">
          <Badge variant="default" className="bg-emerald-500">
            {t('billing.plans.current')}
          </Badge>
        </div>
      )}
      <CardHeader>
        <CardTitle className="text-xl">{plan.displayName}</CardTitle>
        <CardDescription>{plan.description}</CardDescription>
        <div className="mt-4">
          <span className="text-3xl font-bold">${plan.priceInCents}</span>
          <span className="text-slate-500 ml-1">/{t('billing.month')}</span>
        </div>
      </CardHeader>
      <CardContent>
        {features && (
          <ul className="space-y-2">
            {(features.unlimitedUsers !== false) && (
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                <span className="text-sm">{t('billing.plans.unlimitedUsers')}</span>
              </li>
            )}
            {features.storage && (
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                <span className="text-sm">{features.storage}GB {t('billing.plans.storage')}</span>
              </li>
            )}
            {features.ram && (
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                <span className="text-sm">{features.ram}GB {t('billing.plans.ram')}</span>
              </li>
            )}
            {features.maxStaffUsers !== undefined && (
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                <span className="text-sm">
                  {features.maxStaffUsers === -1 
                    ? t('billing.plans.unlimitedStaffMembers')
                    : `${features.maxStaffUsers} ${t('billing.plans.staffMembers')}`
                  }
                </span>
              </li>
            )}
          </ul>
        )}
      </CardContent>
      <CardFooter>
        {isCurrentPlan ? (
          <Button variant="outline" className="w-full" disabled>
            {t('billing.plans.currentPlan')}
          </Button>
        ) : canUpgrade ? (
          <Button
            variant={isSelected ? 'default' : 'outline'}
            className="w-full"
            onClick={onSelect}
          >
            {t('billing.plans.selectPlan')}
            <ArrowUpRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button variant="outline" className="w-full" disabled>
            {t('billing.plans.notAvailable')}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

